import { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { EntryFormDialog } from '@/components/common/EntryFormDialog';
import { createUser, updateUser } from '../api/users';
import { useRoleLookup } from '../hooks/useRoles';
import type { UserDto, CreateUserFormData, UpdateUserFormData } from '../types';

interface UserFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user?: UserDto | null;
  onSuccess: () => void;
}

interface FormState {
  username: string;
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone: string;
  roleId: string;
  commissionRate: string;
  taxRate: string;
  hireDate: string;
  isActive: boolean;
}

const initialFormState: FormState = {
  username: '',
  email: '',
  password: '',
  firstName: '',
  lastName: '',
  phone: '',
  roleId: '',
  commissionRate: '0',
  taxRate: '0',
  hireDate: '',
  isActive: true,
};

export function UserForm({ open, onOpenChange, user, onSuccess }: UserFormProps) {
  const [formData, setFormData] = useState<FormState>(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const { data: roles } = useRoleLookup();

  const isEditMode = !!user?.id;

  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username,
        email: user.email || '',
        password: '',
        firstName: user.firstName,
        lastName: user.lastName,
        phone: user.phone || '',
        roleId: user.roleId.toString(),
        commissionRate: user.commissionRate.toString(),
        taxRate: user.taxRate.toString(),
        hireDate: user.hireDate ? user.hireDate.split('T')[0] : '',
        isActive: user.isActive,
      });
    } else {
      setFormData(initialFormState);
    }
    setError('');
  }, [user, open]);

  const handleChange = (field: keyof FormState, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.firstName.trim()) {
      setError('First name is required');
      return;
    }

    if (!formData.lastName.trim()) {
      setError('Last name is required');
      return;
    }

    if (!isEditMode && !formData.username.trim()) {
      setError('Username is required');
      return;
    }

    if (!formData.roleId) {
      setError('Role is required');
      return;
    }

    if (!isEditMode && !formData.password.trim()) {
      setError('Password is required for new users');
      return;
    }

    const commissionRate = parseFloat(formData.commissionRate);
    if (isNaN(commissionRate) || commissionRate < 0 || commissionRate > 100) {
      setError('Commission rate must be between 0 and 100');
      return;
    }

    const taxRate = parseFloat(formData.taxRate);
    if (isNaN(taxRate) || taxRate < 0 || taxRate > 100) {
      setError('Tax rate must be between 0 and 100');
      return;
    }

    setIsSubmitting(true);

    try {
      if (isEditMode && user) {
        const updateData: UpdateUserFormData = {
          email: formData.email || undefined,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone || undefined,
          roleId: parseInt(formData.roleId),
          commissionRate: commissionRate,
          taxRate: taxRate,
          hireDate: formData.hireDate || undefined,
          isActive: formData.isActive,
        };
        await updateUser(user.id, updateData);
      } else {
        const createData: CreateUserFormData = {
          username: formData.username,
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone || undefined,
          roleId: parseInt(formData.roleId),
          commissionRate: commissionRate,
          taxRate: taxRate,
          hireDate: formData.hireDate || undefined,
        };
        await createUser(createData);
      }
      onSuccess();
      onOpenChange(false);
    } catch (err) {
      const errorObj = err as { response?: { data?: { message?: string } } };
      setError(errorObj.response?.data?.message || 'Failed to save user');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <EntryFormDialog
      isOpen={open}
      onClose={() => onOpenChange(false)}
      title={isEditMode ? 'Edit User' : 'Add User'}
      error={error}
      isSubmitting={isSubmitting}
      submitLabel="Add User"
      editLabel="Save Changes"
      isEditing={isEditMode}
      onSubmit={handleSubmit}
      maxWidth="max-w-lg"
    >
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="firstName">First Name *</Label>
          <Input
            id="firstName"
            value={formData.firstName}
            onChange={(e) => handleChange('firstName', e.target.value)}
            placeholder="First name"
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="lastName">Last Name *</Label>
          <Input
            id="lastName"
            value={formData.lastName}
            onChange={(e) => handleChange('lastName', e.target.value)}
            placeholder="Last name"
            required
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="username">Username *</Label>
        <Input
          id="username"
          value={formData.username}
          onChange={(e) => handleChange('username', e.target.value)}
          placeholder="Username"
          required
          disabled={isEditMode}
        />
        {isEditMode && (
          <p className="text-xs text-muted-foreground">Username cannot be changed</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            placeholder="email@example.com"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone">Phone</Label>
          <Input
            id="phone"
            type="tel"
            value={formData.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
            placeholder="(555) 123-4567"
          />
        </div>
      </div>

      {!isEditMode && (
        <div className="space-y-2">
          <Label htmlFor="password">Password *</Label>
          <Input
            id="password"
            type="password"
            value={formData.password}
            onChange={(e) => handleChange('password', e.target.value)}
            placeholder="Password"
            required
          />
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="roleId">Role *</Label>
        <Select
          id="roleId"
          value={formData.roleId}
          onChange={(e) => handleChange('roleId', e.target.value)}
        >
          <option value="">Select a role</option>
          {roles.map((role) => (
            <option key={role.id} value={role.id}>
              {role.name}
            </option>
          ))}
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="commissionRate">Commission %</Label>
          <Input
            id="commissionRate"
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={formData.commissionRate}
            onChange={(e) => handleChange('commissionRate', e.target.value)}
            placeholder="0"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="taxRate">Tax Rate %</Label>
          <Input
            id="taxRate"
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={formData.taxRate}
            onChange={(e) => handleChange('taxRate', e.target.value)}
            placeholder="0"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="hireDate">Hire Date</Label>
        <Input
          id="hireDate"
          type="date"
          value={formData.hireDate}
          onChange={(e) => handleChange('hireDate', e.target.value)}
        />
      </div>

      {isEditMode && (
        <div className="flex items-center space-x-2">
          <Checkbox
            id="isActive"
            checked={formData.isActive}
            onChange={(e) => handleChange('isActive', e.target.checked)}
          />
          <Label htmlFor="isActive" className="text-sm font-normal">
            Active
          </Label>
        </div>
      )}
    </EntryFormDialog>
  );
}
